import { formatPartnerDisplayDateTime, formatPartnerTableDate } from './partnerDateUtils';
import { getPaeQueueDisplayStatus } from './partnerProjectStatusUtils';

export { getPaeQueueDisplayStatus };

const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadCsv = (filename, columns, rows) => {
  const header = columns.map((column) => escapeCsv(column.label)).join(',');
  const dataRows = (rows || []).map((row) => (
    columns.map((column) => escapeCsv(column.getValue(row))).join(',')
  ));

  // UTF-8 BOM helps Excel open special characters correctly.
  // One physical row per project (no wrapped multi-line cells).
  const csvBody = `\uFEFF${[header, ...dataRows].join('\r\n')}`;
  const blob = new Blob([csvBody], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatProducts = (products) => (products || []).filter(Boolean).join('; ');

const getPartnerName = (project) => project.partnerName || '';

const getActivityNote = (entry) => (
  String(entry?.psmNote || entry?.note || entry?.message || entry?.text || '').trim()
);

// Keep Excel/Sheets rows short: flatten newlines/tabs so Wrap Text is not needed.
const cleanExportText = (value) => String(value || '').replace(/[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim();

const getAdditionalNotesForExport = (project) => {
  const direct = cleanExportText(project?.additionalNotes);
  if (direct) {
    return direct;
  }

  const submitted = (project?.activityLog || []).find(
    (entry) => String(entry?.action || '').trim().toLowerCase() === 'submitted',
  );
  return cleanExportText(getActivityNote(submitted));
};

const getActivityEntryTimestamp = (entry) => (
  entry?.performedAt || entry?.timestamp || entry?.createdAt || entry?.completedAt || null
);

const toActivityTimestampMs = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const getSortedActivityEntries = (project) => {
  const entries = Array.isArray(project?.activityLog) ? [...project.activityLog] : [];
  return entries.sort((left, right) => {
    const leftMs = toActivityTimestampMs(getActivityEntryTimestamp(left)) ?? 0;
    const rightMs = toActivityTimestampMs(getActivityEntryTimestamp(right)) ?? 0;
    return leftMs - rightMs;
  });
};

// Last Activity Date = timestamp of the last Activity Log action (same source as the log column).
const getLastActivityDateValue = (project) => {
  const entries = getSortedActivityEntries(project);
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const timestamp = getActivityEntryTimestamp(entries[index]);
    if (toActivityTimestampMs(timestamp) !== null) {
      return timestamp;
    }
  }
  return project?.updatedAt || project?.submittedAt || null;
};

const formatLastActivityDateForExport = (project) => {
  const value = getLastActivityDateValue(project);
  if (!value) {
    return '';
  }
  const formatted = formatPartnerDisplayDateTime(value);
  return formatted && formatted !== '-' ? formatted : '';
};

const formatActivityAuthorShort = (author) => {
  const value = cleanExportText(author);
  if (!value) {
    return '';
  }
  const atIndex = value.indexOf('@');
  return atIndex > 0 ? value.slice(0, atIndex) : value;
};

const formatActivityActionShort = (action, entry) => {
  const normalized = String(action || '').trim().toLowerCase();
  const status = String(entry?.projectStatus || '').trim();

  switch (normalized) {
    case 'submitted':
      return 'Created';
    case 'received':
      return 'Received';
    case 'approved':
    case 'approve':
      return 'Approved';
    case 'project_updated':
      return 'Updated';
    case 'info_requested':
    case 'request_info':
      return 'Info requested';
    case 'note_added':
    case 'add_note':
    case 'psm_note':
      return 'PAE note';
    case 'status_changed':
    case 'update_status':
      return status ? `Status: ${status}` : 'Status changed';
    default:
      return String(action || 'Activity').replace(/_/g, ' ');
  }
};

const formatActivityChangesShort = (changes) => {
  if (!Array.isArray(changes) || !changes.length) {
    return '';
  }

  const labels = changes
    .map((change) => String(change?.label || '').trim())
    .filter(Boolean);

  if (!labels.length) {
    return `${changes.length} fields`;
  }
  if (labels.length <= 2) {
    return labels.join(', ');
  }
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
};

const formatActivityEntry = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const action = String(entry.action || '').trim().toLowerCase();
  const when = formatPartnerTableDate(getActivityEntryTimestamp(entry));
  const label = formatActivityActionShort(action, entry);
  const author = formatActivityAuthorShort(entry.performedBy || entry.author);
  const changes = formatActivityChangesShort(entry.changes);

  const parts = [];
  if (when && when !== '-') {
    parts.push(when);
  }
  parts.push(label);
  if (author) {
    parts.push(author);
  }
  if (changes) {
    parts.push(changes);
  }

  return parts.join(' - ');
};

const formatActivityLogForExport = (project) => {
  const entries = getSortedActivityEntries(project);
  if (!entries.length) {
    return '';
  }

  // All activities, short, chronological, single line (matches Last Activity Date source).
  return cleanExportText(
    entries
      .map((entry) => formatActivityEntry(entry))
      .filter(Boolean)
      .join(' | '),
  );
};

/** Shared columns for My Projects + PAE Queue exports (detail-page layout fields). */
const SHARED_PROJECT_EXPORT_COLUMNS = [
  { label: 'Partner Name', getValue: getPartnerName },
  { label: 'Partner Company', getValue: (project) => project.companyName || '' },
  { label: 'CSN', getValue: (project) => project.csn || '' },
  { label: 'Partner Email', getValue: (project) => project.partnerEmail || '' },
  { label: 'Customer', getValue: (project) => project.customerName || '' },
  { label: 'Project Lead Name', getValue: (project) => project.projectLeadName || '' },
  { label: 'Project Lead Email', getValue: (project) => project.projectLeadEmail || '' },
  { label: 'Actual Customer Name', getValue: (project) => project.actualCustomerName || '' },
  { label: 'Actual Customer CSN', getValue: (project) => project.actualCustomerCsn || '' },
  {
    label: 'Salesforce Opportunity ID',
    getValue: (project) => project.salesforceOpportunityId || '',
  },
  { label: 'Partner Service', getValue: (project) => project.partnerServiceName || '' },
  { label: 'Products', getValue: (project) => formatProducts(project.productsInScope) },
  { label: 'Engagement', getValue: (project) => project.engagementType || '' },
  { label: 'Deployment Model', getValue: (project) => project.deploymentModel || '' },
  { label: 'Project Status', getValue: (project) => project.projectStatus || '' },
  { label: 'Queue Status', getValue: (project) => getPaeQueueDisplayStatus(project) },
  {
    label: 'Est Start Date',
    getValue: (project) => formatPartnerTableDate(project.estimatedStartDate),
  },
  {
    label: 'Est Go-Live Date',
    getValue: (project) => formatPartnerTableDate(project.estimatedGoliveDate),
  },
  {
    label: 'Act Start Date',
    getValue: (project) => formatPartnerTableDate(project.actualStartDate),
  },
  {
    label: 'Act Go-Live Date',
    getValue: (project) => formatPartnerTableDate(project.actualGoliveDate),
  },
  {
    label: 'Completed Date',
    getValue: (project) => formatPartnerTableDate(project.completedDate),
  },
  { label: 'Partner Description', getValue: (project) => cleanExportText(project.projectDescription) },
  { label: 'Competitive Context', getValue: (project) => cleanExportText(project.competitiveContext) },
  { label: 'Additional Notes', getValue: getAdditionalNotesForExport },
  { label: 'PAE', getValue: (project) => project.partnerAdoptionEngineer || '' },
  { label: 'Region', getValue: (project) => project.region || '' },
  {
    label: 'Submitted At',
    getValue: (project) => formatPartnerTableDate(project.submittedAt),
  },
  { label: 'Submitted By', getValue: (project) => project.submittedBy || '' },
  {
    label: 'Last Activity Date',
    getValue: formatLastActivityDateForExport,
  },
  { label: 'Activity Log', getValue: formatActivityLogForExport },
];

export const exportMyProjectsCsv = (projects) => {
  downloadCsv('my-projects-export.csv', SHARED_PROJECT_EXPORT_COLUMNS, projects);
};

export const exportPaeQueueCsv = (projects) => {
  downloadCsv('pae-queue-export.csv', SHARED_PROJECT_EXPORT_COLUMNS, projects);
};
